import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, X } from 'lucide-react';
import InfoTooltip from './InfoTooltip';

const LocationFilter = ({ 
  zipcode, 
  radius, 
  onZipcodeChange, 
  onRadiusChange, 
  onClear,
  states = [],
  onStatesChange,
  availableStates = [],
  disabled = false 
}) => {
  const [zipcodeError, setZipcodeError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState(null);

  // Validate zipcode format
  const validateZipcode = useCallback((zip) => {
    if (!zip) return true; // Empty is valid (optional filter)
    
    // Remove all non-digit characters
    const cleanZip = zip.replace(/\D/g, '');
    
    // US zipcodes are 5 digits (basic) or 9 digits (ZIP+4)
    return cleanZip.length === 5 || cleanZip.length === 9;
  }, []);

  // Validate zipcode with API
  const validateZipcodeWithAPI = useCallback(async (zip) => {
    setIsValidating(true);
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE}/location/geocode/${zip}`);
      const result = await response.json();
      
      if (!result.success) {
        setZipcodeError(result.error || 'Invalid zipcode');
      } else {
        setZipcodeError(''); // Clear any previous errors
      }
    } catch (error) {
      console.error('Error validating zipcode:', error);
      setZipcodeError('Error validating zipcode');
    } finally {
      setIsValidating(false);
    }
  }, []);

  // Handle zipcode input changes
  const handleZipcodeChange = useCallback((e) => {
    const value = e.target.value;
    onZipcodeChange(value);
    
    // Clear previous timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    
    // Clear error immediately for empty input
    if (!value) {
      setZipcodeError('');
      return;
    }
    
    // Validate format
    if (!validateZipcode(value)) {
      setZipcodeError('Please enter a valid US zipcode (5 or 9 digits)');
      return;
    }
    
    // Clear format error
    setZipcodeError('');
    
    // Set up debounced validation for geocoding
    const timer = setTimeout(() => {
      if (value && validateZipcode(value)) {
        validateZipcodeWithAPI(value);
      }
    }, 1000); // 1 second debounce
    
    setDebounceTimer(timer);
  }, [onZipcodeChange, validateZipcode, debounceTimer, validateZipcodeWithAPI]);

  // Handle radius change
  const handleRadiusChange = useCallback((e) => {
    const value = parseFloat(e.target.value);
    onRadiusChange(value);
  }, [onRadiusChange]);

  // Handle state change
  const handleStateChange = useCallback((value, checked) => {
    if (onStatesChange) {
      onStatesChange(value, checked);
    }
  }, [onStatesChange]);

  // Handle clear
  const handleClear = useCallback(() => {
    onZipcodeChange('');
    onRadiusChange(25); // Reset to default
    setZipcodeError('');
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      setDebounceTimer(null);
    }
    onClear();
  }, [onZipcodeChange, onRadiusChange, onClear, debounceTimer]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  const hasLocationFilter = zipcode && zipcode.trim() !== '';
  const hasStateFilter = states && states.length > 0;

  return (
    <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
      <div className="flex items-center gap-2 mb-3">
        <MapPin size={16} className="text-green-600" />
        <h3 className="font-semibold text-gray-900 text-sm">Location</h3>
        <InfoTooltip description="Filter products by distance from your location or by specific states. Enter your zipcode and set a search radius, or select states to see products available in those locations." />
      </div>
      
      {/* Zipcode Input */}
      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Zipcode
        </label>
        <div className="relative">
          <input
            type="text"
            placeholder="Enter zipcode (e.g., 02101)"
            value={zipcode || ''}
            onChange={handleZipcodeChange}
            disabled={disabled}
            className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 ${
              zipcodeError 
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                : 'border-gray-300 focus:ring-green-500 focus:border-green-500'
            } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            maxLength={10}
          />
          {isValidating && (
            <div className="absolute right-2 top-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
            </div>
          )}
        </div>
        {zipcodeError && (
          <p className="text-xs text-red-600 mt-1">{zipcodeError}</p>
        )}
      </div>
      
      {/* Radius Slider */}
      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-700 mb-2">
          Search Radius: {radius} miles
        </label>
        <div className="relative">
          <input
            type="range"
            min="1"
            max="100"
            step="1"
            value={radius || 25}
            onChange={handleRadiusChange}
            disabled={disabled || !hasLocationFilter}
            className={`w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider ${
              disabled || !hasLocationFilter ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            style={{
              background: `linear-gradient(to right, #10b981 0%, #10b981 ${((radius || 25) - 1) / 99 * 100}%, #e5e7eb ${((radius || 25) - 1) / 99 * 100}%, #e5e7eb 100%)`
            }}
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>1 mi</span>
            <span>50 mi</span>
            <span>100 mi</span>
          </div>
        </div>
      </div>
      
      {/* State Filter */}
      {availableStates.length > 0 && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-2">
            States
          </label>
          <div className="max-h-32 overflow-y-auto space-y-1" style={{scrollbarWidth: 'thin'}}>
            {availableStates.slice(0, 20).map((state) => (
              <label key={state} className="flex items-center space-x-2 cursor-pointer py-1">
                <input
                  type="checkbox"
                  checked={states.includes(state)}
                  onChange={(e) => handleStateChange(state, e.target.checked)}
                  disabled={disabled}
                  className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                />
                <span className="text-sm text-gray-700">{state}</span>
              </label>
            ))}
            {availableStates.length > 20 && (
              <p className="text-xs text-gray-500 mt-1">
                Showing first 20 of {availableStates.length} states
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Active Filter Display */}
      {(hasLocationFilter || hasStateFilter) && (
        <div className="space-y-2">
          {hasLocationFilter && (
            <div className="flex items-center justify-between p-2 bg-green-50 rounded-md border border-green-200">
              <div className="flex items-center gap-2">
                <MapPin size={14} className="text-green-600" />
                <span className="text-sm text-green-800">
                  Within {radius} miles of {zipcode}
                </span>
              </div>
              <button
                onClick={handleClear}
                disabled={disabled}
                className="text-green-600 hover:text-green-800 disabled:opacity-50"
              >
                <X size={14} />
              </button>
            </div>
          )}
          {hasStateFilter && (
            <div className="p-2 bg-blue-50 rounded-md border border-blue-200">
              <div className="flex items-center gap-2 mb-1">
                <MapPin size={14} className="text-blue-600" />
                <span className="text-sm text-blue-800 font-medium">States:</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {states.map(state => (
                  <span key={state} className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    {state}
                    <button 
                      onClick={() => handleStateChange(state, false)}
                      disabled={disabled}
                      className="ml-1 text-blue-600 hover:text-blue-800 disabled:opacity-50"
                    >
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Help Text */}
      {!hasLocationFilter && !hasStateFilter && (
        <p className="text-xs text-gray-500 mt-2">
          Enter your zipcode to filter by distance, or select states to filter by location
        </p>
      )}
    </div>
  );
};

export default LocationFilter;
